import {Injectable} from "@angular/core";
import {AuthenticationService} from "./authentication/authentication.service";
import {environment} from "../environments/environment";
import {HttpClient} from "@angular/common/http";
import {Router} from "@angular/router";
import {Order, OrderStatusEnum} from "./store/state/order.state";
import {AesEncryptorService} from "./umbrella-common/services/aes-encryptor.service";
import {TranslateService} from "@ngx-translate/core";
import {ClientResourcesFetchService} from "./umbrella-common/services/client-resources-fetch.service";
import {LocaleService} from "./umbrella-common/services/locale.service";
import {ConfigurationProvider} from "./configuration/configuration.provider";
import {first} from "rxjs/operators";
import {Location} from "@angular/common";
import {GlobalSpinnerTriggeringService} from "./global-spinner-triggering.service";

@Injectable()
export class NavigationService {

	private orderNavigationMap: Map<OrderStatusEnum, string[]> = new Map();
	private auditNavigationMap: Map<OrderStatusEnum, string[]> = new Map();
	private dekraEmployeeInitialNavigationMap: Map<string, string[]> = new Map([
		['ROLE_APPLICATION_REVIEWER', ["application-reviewer", "dashboard", "ar"]],
		['ROLE_CLIENT_SERVICE', ["client-service", "dashboard"]],
		['ROLE_TECHNICAL_REVIEWER', ["technical-reviewer", "dashboard"]],
		['ROLE_CERT_OFFICIAL', ["technical-reviewer", "dashboard"]],
		['ROLE_SALES_PERSON', ["application-reviewer", "dashboard", "sp"]],
		['ROLE_PRIVACY_MANAGER', ["privacy-manager", "dashboard"]],
		['ROLE_ADMIN', ["app-settings"]]
	]);

	constructor(private http: HttpClient, private authService: AuthenticationService, private router: Router, private aesEncryptor: AesEncryptorService, private translateService: TranslateService,
				private clientResourcesFetchService: ClientResourcesFetchService, private localeService: LocaleService, private configProvider: ConfigurationProvider, private location: Location,
				private globalSpinnerTrigger: GlobalSpinnerTriggeringService) {

		this.orderNavigationMap.set(OrderStatusEnum.LEAD, ['order', 'estimate']);
		this.orderNavigationMap.set(OrderStatusEnum.EDIT_LEAD, ['order', 'estimate-evaluation']);
		this.orderNavigationMap.set(OrderStatusEnum.NEW, ['order', 'additional-info']);
		this.orderNavigationMap.set(OrderStatusEnum.ORDER_CONFIRMATION, ['order', 'confirmation']);
		this.orderNavigationMap.set(OrderStatusEnum.UPLOAD_TRANSFER_DOCUMENTS, ['order', 'upload-documents']);
		this.orderNavigationMap.set(OrderStatusEnum.UNDER_APPLICATION_REVIEW, ['order', 'under-review']);
		this.orderNavigationMap.set(OrderStatusEnum.DECLINED_BY_CLIENT, ['order', 'declined']);
		this.orderNavigationMap.set(OrderStatusEnum.ACCEPTED_BY_APPLICATION_REVIEWER, ['order', 'active-order']);
		this.orderNavigationMap.set(OrderStatusEnum.TRANSFER_CHECK, ['order', 'active-order']);
		this.orderNavigationMap.set(OrderStatusEnum.TRANSFER_DECISION_POSITIVE, ['order', 'active-order']);
		this.orderNavigationMap.set(OrderStatusEnum.DECLINED_BY_APPLICATION_REVIEWER, ['order', 'declined-by-reviewer']);
		this.orderNavigationMap.set(OrderStatusEnum.REVIEW_CHANGE_PROPOSAL, ['order', 'review-change-proposal']);
		this.orderNavigationMap.set(OrderStatusEnum.AUDIT_EVENT_CONFIRMATION, ['order', 'audit-event-confirmation']);
		this.orderNavigationMap.set(OrderStatusEnum.AUDIT_EVENT_CONFIRMED, ['order', 'audit-event-confirmation']);
		this.orderNavigationMap.set(OrderStatusEnum.AUDIT_EVENT_CHANGED, ['order', 'audit-event-confirmation']);
		this.orderNavigationMap.set(OrderStatusEnum.AUDIT_EVENT_CLOSED, ['order', 'audit-event-confirmation']);
		this.orderNavigationMap.set(OrderStatusEnum.TRANSLATION, ['order', 'under-review']);
		this.orderNavigationMap.set(OrderStatusEnum.REVIEW_CHANGE_PROPOSAL_ACCEPTED_BY_CLIENT, ['order', 'under-review']);

		this.auditNavigationMap.set(OrderStatusEnum.DRAFT, ['client-service', 'audit', 'additional-questions']);
		this.auditNavigationMap.set(OrderStatusEnum.CONFIRMED, ['client-service', 'audit', 'information']);
		this.auditNavigationMap.set(OrderStatusEnum.PREVIEW, ['client-service', 'audit', 'information']);
		this.auditNavigationMap.set(OrderStatusEnum.FORECASTED, ['client-service', 'audit', 'information']);
		this.auditNavigationMap.set(OrderStatusEnum.SENT, ['client-service', 'audit', 'information']);
		this.auditNavigationMap.set(OrderStatusEnum.DECLINED, ['client-service', 'audit', 'information']);
		this.auditNavigationMap.set(OrderStatusEnum.CLIENT_CHANGE, ['client-service', 'audit', 'information']);
		this.auditNavigationMap.set(OrderStatusEnum.CLOSED, ['client-service', 'audit', 'closed']);
	}

	initialNavigate(forceRefresh?: boolean, skipSetup?: boolean): void {
		if (this.authService.isLoggedUserClient(false)) {
			this.navigateToProperOrderStep(null, forceRefresh, skipSetup);
		} else if (this.authService.isDekraEmployeeLogged()) {
			this.navigateToProperDekraEmployeePage(forceRefresh);
		} else {
			this.navigateToGetAnEstimatePage(forceRefresh);
		}
	}

	navigateToProperOrderStep(returnUrl?: string, forceRefresh?: boolean, skipSetup?: boolean): void {
		this.clientResourcesFetchService.fetch().subscribe(validOrdersResponse => {
			if (returnUrl) {
				this.router.navigateByUrl(returnUrl);
			} else {
				const validOrder = validOrdersResponse.validResources[validOrdersResponse.validResources.length - 1];

				if (validOrder) {
					let commands: any [] = this.orderNavigationMap.get(validOrder.status);
					commands = [commands[0], validOrder.correlationId, commands[1]];
					forceRefresh && commands.push({forceRefresh});

					const extras: any = {};
					if (skipSetup) {
						extras.queryParams = { skipSetup: true };
						extras.queryParamsHandling = 'merge';
					}

					this.router.navigate(commands, extras);
				} else {
					let baseUrl = this.getBaseApplicationUrl();
					this.router.navigateByUrl(baseUrl + "/order/estimate");
				}
			}
		});
	}

	navigateToDashboard(reassignWarning?: boolean, returnUrl?: string): Promise<boolean> {
		if (returnUrl) {
			return this.router.navigateByUrl(returnUrl);
		} else {
			let commands = this.dekraEmployeeInitialNavigationMap.get(this.authService.getUserCurrentRole());
			return this.router.navigate(commands, reassignWarning ? {queryParams: {reassignWarning: true}} : {});
		}
	}

	navigateToTechnicalReviewerDashboard(returnUrl?: string): Promise<boolean> {
		if (returnUrl) {
			return this.router.navigateByUrl(returnUrl);
		} else {
			return this.router.navigate(["technical-reviewer", "dashboard"]);
		}
	}

	navigateToClientServiceDashboard(returnUrl?: string): Promise<boolean> {
		if (returnUrl) {
			return this.router.navigateByUrl(returnUrl);
		} else {
			return this.router.navigate(["client-service", "dashboard", {forceRefresh: true}]);
		}
	}

	navigateToClientServiceDashboardWithReassignWarning(): Promise<boolean> {
		return this.router.navigate(["client-service", "dashboard", {forceRefresh: true}], {queryParams: {reassignWarning: true}});
	}

	navigateToTechnicalReviewerDashboardWithReassignWarning(): Promise<boolean> {
		return this.router.navigate(["technical-reviewer", "dashboard", {forceRefresh: true}], {queryParams: {reassignWarning: true}});
	}

	navigateOnLoggedOut(): void {
		this.router.navigate(['order', 'estimate']);
	}

	navigateOnAccountAlreadyVerified(): void {
		if (this.authService.isLoggedIn()) {
			this.navigateToProperOrderStep();
		} else {
			this.router.navigate(['login'], {queryParams: {accountAlreadyVerified: true}});
		}
	}

	navigateToAccountSettings() {
		if (this.authService.isLoggedUserClient(true)) {
			this.navigateClientToAccountSettings();
		} else {
			this.router.navigate(['account-settings']);
		}
	}

	private navigateClientToAccountSettings() {
		this.clientResourcesFetchService.fetch().subscribe(validOffersResponse => {
			const validOrder = validOffersResponse.validResources[validOffersResponse.validResources.length - 1];
			this.router.navigate(['account-settings', validOrder.correlationId]);
		});
	}

	navigateToResetPassword(): void {
		this.router.navigate(['password-reset-view']);
	}

	navigateToResendActivationEmail(): void {
		this.router.navigate(['resend-activation-email']);
	}

	navigateToPasswordChangedSuccess(): void {
		this.router.navigate(['password-changed-view']);
	}

	public getClientOrderNavigationCommand(order: Order): string[] {
		return this.orderNavigationMap.get(order.status);
	}

	navigateToErrorPage(): void {
		this.router.navigate(['error-page']);
	}

	navigateToSessionExpired(): void {
		const email = localStorage.getItem('email');
		this.authService.logout();
		this.router.navigate(['login'], {queryParams: {sessionExpired: true, email: this.aesEncryptor.encrypt(email, true)}});
	}

	navigateToAdminPanel() {
		this.router.navigate(['admin']);
	}

	navigateToGetAnEstimatePage(reloadBrowser?: boolean) {
		if (reloadBrowser) {
			let baseUrl = this.getBaseApplicationUrl();
			window.location.replace(`${baseUrl}/order/estimate`);
		} else {
			this.router.navigate(["order", "estimate"], {state: {afterDecline: true}});
		}
	}

	navigateToTechnicalReviewPage(offerUuid: string, readonly: boolean) {
		this.router.navigate(['technical-reviewer', 'offer', offerUuid, 'review', {readonly}], {queryParams: {forceRefresh: true}});
	}

	goToLoginPage() {
		this.router.navigate(['login']);
	}

	goToOrderDeclinedByClientView(correlationId: string) {
		this.router.navigate(['order', correlationId, 'declined']);
	}

	goToUrl(url: string): void {
		this.router.navigateByUrl(url);
	}

	navigateToDekraPage() {
		this.configProvider.getConfiguration().pipe(first()).subscribe(config => {
			window.open(config.dekraMotherPage, "_blank");
		});
	}

	goToSalesLead(correlationId: string, editMode: boolean, forceRefresh?: boolean) {
		if (editMode) {
			this.router.navigate(['application-reviewer', correlationId, 'sales-lead-edit']);
		} else {
			this.router.navigate(['application-reviewer', correlationId, 'sales-lead']);
		}
	}

	navigateToOrderReview(orderCorrelationId: string) {
		this.router.navigate(['application-reviewer', 'review', orderCorrelationId, 'preview-order']);
	}

	navigateToCreateClientAccount() {
		this.router.navigate(['client-service', 'audit', 'creation']);
	}

	navigateToCsAdditionalQuestion(correlationId: string) {
		this.router.navigate(['client-service', 'audit', correlationId, 'additional-questions']);
	}

	navigateToAudit(orderCorrelationId: string, status: OrderStatusEnum) {
		let commands: any [] = this.auditNavigationMap.get(status);
		commands = [commands[0], commands[1], orderCorrelationId, commands[2]];
		this.router.navigate(commands);
	}

	navigateToClientAuditChanges(correlationId: string, token: string) {
		this.router.navigate(['order', correlationId, 'audit-event-changes', token]);
	}

	navigateToTransferReview(correlationId: string, isTechnicalCheck: boolean) {
		this.router.navigate(['technical-reviewer', 'order', correlationId, 'review', isTechnicalCheck ? 'technical-check' : 'transfer-decision']);
	}

	private navigateToProperDekraEmployeePage(forceRefresh?: boolean) {
		const navigationCommand: string[] = this.dekraEmployeeInitialNavigationMap.get(this.authService.getUserCurrentRole());
		if(navigationCommand == null) {
			this.navigateToErrorPage();
		}

		if (forceRefresh) {
			this.globalSpinnerTrigger.setVisible(true);
			let baseUrl = this.getBaseApplicationUrl();
			window.location.replace(`${baseUrl}/${navigationCommand.join('/')}`);
		} else {
			this.router.navigate(navigationCommand);
		}
	}

	goToResendActivationEmailSuccessPage(email: string) {
		this.router.navigateByUrl(`resend-activation-email-success?email=` + this.aesEncryptor.encrypt(email, true));
	}

	navigateToKpisPanel() {
		this.router.navigate(['app-settings', 'kpis']);
	}

	navigateToUserMgmt() {
		this.router.navigate(['app-settings', 'user-management']);
	}

	navigateToConfigPanel() {
		this.router.navigate(['app-settings', 'configuration']);
	}

	getBaseApplicationUrl(): string {
		let locale = this.authService.isDekraEmployeeLogged() ?
			Object.keys(environment.applicationUrl).find(locale => locale.endsWith(this.authService.getUser().countryCode)) :
			this.localeService.currentLocale();

		return environment.applicationUrl[locale];
	}

	navigateToPreviousPage(): void {
		this.location.back();
	}

	navigateToPasswordChangeAfterExpiration(email: string, token: string, isClient: boolean) {
		return this.router.navigate(['password-expired'], {queryParams: {email: email, token: token, isClient: isClient}});
	}

	navigateToChangeProposal(correlationId: string) {
		this.router.navigate(['application-reviewer', 'review', correlationId, 'edit-order']);
	}

	navigateToTranslationsEditPage(correlationId: string) {
		this.router.navigate(['application-reviewer', 'review', correlationId, 'edit-translations']);
	}

	goToMaintenancePage() {
		!this.router.url.includes('maintenance') && this.router.navigate(['maintenance']);
	}
}
